import { Component, Input, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { FormErrorComponent } from '../form-error/form-error.component';

/**
 * IsColorFieldComponent
 *
 * Seletor de cor com preview visual.
 *
 * @example
 * <is-color-field
 *   label="Cor primária"
 *   formControlName="primaryColor"
 * ></is-color-field>
 */
@Component({
  selector: 'is-color-field',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormErrorComponent],
  template: `
    <div class="mb-3">
      <label *ngIf="label" class="form-label">
        {{ label }}
        <span *ngIf="required" class="text-danger ms-1">*</span>
      </label>

      <div class="d-flex align-items-center gap-2">
        <input
          type="color"
          class="form-control form-control-color"
          [class.is-invalid]="hasError"
          [disabled]="isDisabled"
          [value]="value"
          (input)="handleInput($event)"
          (blur)="handleBlur()"
        >
        <span class="form-text text-muted small">{{ value || '#000000' }}</span>
      </div>

      <small *ngIf="helpText && !hasError" class="form-text text-muted">{{ helpText }}</small>
      <is-form-error *ngIf="control" [control]="control"></is-form-error>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorFieldComponent),
      multi: true,
    },
  ],
})
export class ColorFieldComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() required = false;
  @Input() helpText = '';
  @Input() control: AbstractControl | null = null;

  value = '#000000';
  isDisabled = false;

  private onChange: (val: string) => void = () => {};
  private onTouched: () => void = () => {};

  get hasError(): boolean {
    return !!(this.control?.invalid && (this.control?.dirty || this.control?.touched));
  }

  writeValue(val: string): void {
    this.value = val || '#000000';
  }

  registerOnChange(fn: (val: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
  }

  handleInput(event: Event): void {
    const val = (event.target as HTMLInputElement).value;
    this.value = val;
    this.onChange(val);
  }

  handleBlur(): void {
    this.onTouched();
  }
}
